/**
 * `/plans/$planId` — the plan builder.
 *
 * A path, a search validator and a screen. The read policy for the plan tree, the phase parameter's
 * defaulting and every rule about what the builder shows all live in `pages/plan-builder`; putting
 * any of them here would put half a slice in a route file.
 *
 * **No loader.** The tree is `staleTime: Infinity` with structural sharing off (execution ADR §4)
 * and, from PR-B1, the store owns it once loaded. A loader would fetch it a second time on every
 * navigation the router considers a fresh match, and would do it outside the component that holds
 * the draft.
 *
 * ── The parameter is parsed HERE, and `null` means "not a plan id" ───────────────────────────
 * The screen takes a target rather than reading `useParams` itself, because it is now mounted by two
 * routes — this one and `/plans/nowy`, which has no parameter at all. Parsing at the route file is
 * what keeps that difference a wiring detail: `null` reaches the screen as an unusable address and
 * gets the error state, exactly as it did before.
 */

import { createFileRoute, useParams } from '@tanstack/react-router';

import { PlanBuilderScreen, validatePlanBuilderSearch } from '~/pages/plan-builder';

export const Route = createFileRoute('/_shell/plans/$planId')({
  validateSearch: validatePlanBuilderSearch,
  component: StoredPlanRoute,
});

function StoredPlanRoute() {
  const { planId } = useParams({ from: '/_shell/plans/$planId' });
  // A route parameter is a string and not necessarily a number at all. `null` short-circuits the
  // read rather than firing a request that can only 404.
  const id = Number(planId);
  // The raw parameter travels with the parsed one. `planId` is deliberately lossy — it is `null` for
  // everything that is not a plan id — and the screen's heading falls back to it, so without the raw
  // string `/plans/abc` announced itself as `#` and titled its tab `# · MoveBuddy`.
  return (
    <PlanBuilderScreen
      target={{
        kind: 'stored',
        planId: Number.isInteger(id) && id > 0 ? id : null,
        rawId: planId,
      }}
    />
  );
}
